import "./booking-summary.scss";

import { AvailableSlotDto } from "@api/api";
import { RegistrationFormSchema } from "@factory/form/form.rule";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ReactNode, useMemo } from "react";
import { useFormContext } from "react-hook-form";

interface BookingSummaryProps {
  tenant: string;
  clinicName?: string;
  selectedDate: Date;
  selectedSlot: AvailableSlotDto;
  selectedService?: string;
  serviceName?: string;
  onBack: () => void;
  onChangeDate: () => void;
  onChangeTime: () => void;
  children: ReactNode; // Form content
  isSubmitting: boolean;
  onSubmit: () => void;
  showSuccess: boolean;
  bookingReference?: string;
  onCloseSuccess: () => void;
  contactEmail?: string;
  contactPhone?: string;
  facebookLink?: string;
  /** If true, hides time display in the booking summary */
  hideTime?: boolean;
}

const formatTime = (timeStr: string): string => {
  const [hours, minutes] = timeStr.split(":").map(Number);
  const period = hours >= 12 ? "PM" : "AM";
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

const formatDate = (date: Date): string => {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const calculateDuration = (startTime: string, endTime: string): string => {
  const [startH, startM] = startTime.split(":").map(Number);
  const [endH, endM] = endTime.split(":").map(Number);

  const startMinutes = startH * 60 + startM;
  const endMinutes = endH * 60 + endM;
  const durationMinutes = endMinutes - startMinutes;

  if (durationMinutes < 60) {
    return `${durationMinutes} minutes`;
  } else {
    const hours = Math.floor(durationMinutes / 60);
    const mins = durationMinutes % 60;
    return mins > 0
      ? `${hours}h ${mins}m`
      : `${hours} hour${hours > 1 ? "s" : ""}`;
  }
};

export function BookingSummary({
  tenant,
  clinicName,
  selectedDate,
  selectedSlot,
  selectedService,
  serviceName,
  onBack,
  onChangeDate,
  onChangeTime,
  children,
  isSubmitting,
  onSubmit,
  showSuccess,
  bookingReference,
  onCloseSuccess,
  contactEmail,
  contactPhone,
  facebookLink,
  hideTime = false,
}: BookingSummaryProps) {
  // Get form values for display
  const { watch } = useFormContext<RegistrationFormSchema>();
  const formDate = watch("date");
  const formTime = watch("time");
  const formService = watch("service");

  // Format form values for display
  const formattedDate = useMemo(() => {
    if (formDate) {
      // formDate is a string in YYYY-MM-DD format, convert to Date for formatting
      const date = new Date(formDate + "T00:00:00");
      return formatDate(date);
    }
    return formatDate(selectedDate);
  }, [formDate, selectedDate]);

  const formattedTime = useMemo(() => {
    if (hideTime) return "";
    if (formTime) {
      // formTime is a string in HH:mm format
      return formatTime(formTime + ":00"); // Add seconds for formatTime function
    }
    if (!selectedSlot.start_time) return "";
    return formatTime(selectedSlot.start_time);
  }, [formTime, selectedSlot.start_time, hideTime]);

  const formattedEndTime = useMemo(() => {
    if (hideTime) return "";
    if (formTime) {
      // Calculate end time based on slot duration (assuming 30 min default)
      const [hours, minutes] = formTime.split(":").map(Number);
      const endMinutes = minutes + 30; // Default 30 min duration
      const endHours = hours + Math.floor(endMinutes / 60);
      const finalMinutes = endMinutes % 60;
      const endTime = `${endHours.toString().padStart(2, "0")}:${finalMinutes.toString().padStart(2, "0")}:00`;
      return formatTime(endTime);
    }
    if (!selectedSlot.end_time) return "";
    return formatTime(selectedSlot.end_time);
  }, [formTime, selectedSlot.end_time, hideTime]);

  const duration = useMemo(() => {
    if (hideTime) return "";
    if (formTime) {
      return "30 minutes"; // Default duration
    }
    if (!selectedSlot.start_time || !selectedSlot.end_time) return "";
    return calculateDuration(selectedSlot.start_time, selectedSlot.end_time);
  }, [formTime, selectedSlot, hideTime]);

  const displayServiceName = useMemo(() => {
    if (formService) {
      // Try to find service name from form service code
      return formService; // For now just display the code, could be enhanced to look up name
    }
    return serviceName || selectedService;
  }, [formService, serviceName, selectedService]);

  return (
    <div className="booking-summary">
      <div className="summary-header">
        <button className="back-btn" onClick={onBack} aria-label="Go back">
          <i className="pi pi-arrow-left"></i>
        </button>
        <h3>Complete Your Booking</h3>
      </div>

      {/* <div className="appointment-card">
        <div className="card-header">
          <i className="pi pi-calendar-plus"></i>
          <h4>Appointment Details</h4>
        </div>
        <div className="appointment-details">
          {clinicName && (
            <div className="detail-row">
              <i className="pi pi-building"></i>
              <div className="detail-content">
                <div className="detail-label">Clinic</div>
                <div className="detail-value">{clinicName}</div>
              </div>
            </div>
          )}
          
          <div className="detail-row">
            <i className="pi pi-calendar"></i>
            <div className="detail-content">
              <div className="detail-label">Date</div>
              <div className="detail-value">{formattedDate}</div>
            </div>
            <span className="edit-link" onClick={onChangeDate}>
              Change
            </span>
          </div>

          <div className="detail-row">
            <i className="pi pi-clock"></i>
            <div className="detail-content">
              <div className="detail-label">Time</div>
              <div className="detail-value">
                {formattedTime} - {formattedEndTime}
              </div>
            </div>
            <span className="edit-link" onClick={onChangeTime}>
              Change
            </span>
          </div>

          <div className="detail-row">
            <i className="pi pi-hourglass"></i>
            <div className="detail-content">
              <div className="detail-label">Duration</div>
              <div className="detail-value">{duration}</div>
            </div>
          </div>

          {displayServiceName && (
            <div className="detail-row">
              <i className="pi pi-tag"></i>
              <div className="detail-content">
                <div className="detail-label">Service</div>
                <div className="detail-value">{displayServiceName}</div>
              </div>
            </div>
          )}
        </div>
      </div> */}

      <div className="form-section">
        {/* <h4>
          
          Patient Information
        </h4> */}
        {children}
      </div>

      <div className="action-section">
        <div className="action-buttons">
          <Button
            label="Back"
            icon="pi pi-arrow-left"
            className="p-button-outlined p-button-secondary"
            onClick={onBack}
            disabled={isSubmitting}
          />
          <Button
            label={isSubmitting ? "Booking..." : "Confirm Booking"}
            icon="pi pi-check"
            onClick={onSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
        <p className="disclaimer">
          By confirming this booking, you agree to our terms and conditions. You
          will receive a confirmation notification.
        </p>
      </div>

      <Dialog
        visible={showSuccess}
        onHide={onCloseSuccess}
        modal
        closable
        showHeader={false}
        className="booking-success-modal"
        style={{ width: "420px", maxWidth: "95vw" }}
      >
        <div className="success-content">
          <div className="success-icon">
            <i className="pi pi-check"></i>
          </div>
          <h2>BOOKING IS NOW BEING PROCESSED</h2>
          <p className="success-message">
            Your Appointment has been successfully processed. Please wait for
            our staff to confirm your booking via SMS or CALL.
            <br />
            <br />
            For approved bookings: Cut Off time is 3PM only. Please arrive on
            the venue on time once your booking is confirmed.
            <br />
            <br />
            Thank you.
          </p>

          {bookingReference && (
            <span className="text-xs text-gray-400">
              Ref: {bookingReference}
            </span>
          )}

          <div className="confirmation-details">
            <div className="detail-item">
              <span className="label">Date</span>
              <span className="value">{formattedDate}</span>
            </div>
            {!hideTime && (
              <div className="detail-item">
                <span className="label">Time</span>
                <span className="value">{formattedTime}</span>
              </div>
            )}
            {serviceName && (
              <div className="detail-item">
                <span className="label">Service</span>
                <span className="value">{serviceName}</span>
              </div>
            )}
          </div>

          <div className="success-actions">
            <Button label="Done" icon="pi pi-check" onClick={onCloseSuccess} />
            {facebookLink && (
              <Button
                label="Follow Us on Facebook"
                icon="pi pi-facebook"
                className="p-button-outlined"
                onClick={() => window.open(facebookLink, "_blank")}
              />
            )}
          </div>

          {(contactEmail || contactPhone) && (
            <div className="contact-info">
              Questions? Contact us at{" "}
              {contactEmail && (
                <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
              )}
              {contactEmail && contactPhone && " or "}
              {contactPhone && (
                <a href={`tel:${contactPhone}`}>{contactPhone}</a>
              )}
            </div>
          )}
        </div>
      </Dialog>
    </div>
  );
}

export default BookingSummary;
